import { useEffect, useMemo, useState } from "react";
import { Helmet } from "@/components/Helmet";
import { useLocation, useParams } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { getPublicScanApiBase } from "@/lib/live-scan";

const PUBLIC_SCAN_API_BASE = getPublicScanApiBase();

const LiveScanPdf = () => {
  const { scanId, domain } = useParams<{ scanId: string; domain: string }>();
  const location = useLocation();
  const [status, setStatus] = useState<"idle" | "downloading" | "done" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  const scanToken = useMemo(() => new URLSearchParams(location.search).get("token") || "", [location.search]);

  useEffect(() => {
    if (!scanId) return;
    const fetchPdf = async () => {
      setStatus("downloading");
      setError(null);
      const tokenParam = scanToken ? `?token=${encodeURIComponent(scanToken)}` : "";
      const res = await fetch(`${PUBLIC_SCAN_API_BASE}/scan/${scanId}/report.pdf${tokenParam}`);
      if (!res.ok) {
        throw new Error(res.status === 403 ? "This report link has expired or is invalid." : "Failed to download report.");
      }
      const blob = await res.blob();
      const filename = `ShadowScan-Report-${domain || scanId}.pdf`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setStatus("done");
    };

    fetchPdf().catch((err) => {
      setStatus("error");
      setError(String(err?.message || err));
    });
  }, [domain, scanId, scanToken]);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>Download Report | ShadowScan</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Navigation />
      <main className="flex-grow pt-20">
        <section className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto cyber-card rounded-xl p-6 border-primary/30">
            {status === "downloading" && (
              <p className="text-sm text-muted-foreground">Preparing your PDF report...</p>
            )}
            {status === "done" && (
              <p className="text-sm text-muted-foreground">Your download should begin automatically.</p>
            )}
            {status === "error" && (
              <p className="text-sm text-destructive">{error || "Unable to download report."}</p>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default LiveScanPdf;
